import React, { useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { Grid, Cell } from "react-md";
import { UserInformation, LoginActivity, EmployeeDetails, TeamMemberManagement } from "components/User";
import QAMacCard from "components/User/Mac/QAMacCard";
import { MSpinner, MCard } from "components/Shared";
import { BUSOPS_AGENT_MAC_BONUS_SHOW, MANAGEMENT_CALENDAR_EDIT, QA_TEAM_MAINTENANCE, EMPLOYEE_COURSE_VIEW, AUTHENTICATION_EDIT } from 'typeConstants/rights';
import { userHasRight } from "selectors/rights";
import EmployeeCalendar from "components/Employee/EmployeeCalendar.tsx";
import { getEmployeeDetails } from "requests/employee";
import EmployeeTEAM from "components/Employee/EmployeeTEAM";
import EmployeeCourses from "components/Employee/EmployeeCourses";
import EmployeeMappings from "components/EmployeeCustomerSurveyTypes/EmployeeMappings.tsx";
import { auditQAQueueType, homeownersQAQueueType } from "components/EmployeeCustomerSurveyTypes/AddMapping.tsx";
import { InternetUser, EmployeeDetailDto, InternetUserLoginStatisticsDto } from "interfaces/Mueller.Business.Models.ts";
import { auditQAQueueTypeMSelectOption, homeownersQaQueueTypeMSelectOption } from "typeConstants/employeeCustomerSurveyTypes";

interface AgentProfileProps {
  verticalDisplay: boolean,
  loginStatistics: InternetUserLoginStatisticsDto,
  getLoginStatistics: (internetUserId: number) => InternetUserLoginStatisticsDto,
  isFetchingLoginStatistics: boolean,
  internetUserId: number,
  internetUser: InternetUser,
  getUser: (internetUserId: number) => InternetUser,
  isFetchingInternetUser: boolean,
  isAddingTimeClock: boolean,
  isDeletingTimeClock: boolean,
  canEditCalendarAsManager: boolean,
  showTeamManagementCard: boolean,
  canViewEmployeeCourses: boolean,
  canViewMacBonus: boolean,
  canEditUsers: boolean,
  supervisorName: string,
}

const AgentProfile = (props: AgentProfileProps) => {

  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [cellWidth, setCellWidth] = useState(null);
  const [internetUserId, setInternetUserId] = useState(0);

  const mounted = useRef(true);

  useEffect(() => {
    let userId = props.internetUserId;
    setInternetUserId(userId);
    props.getLoginStatistics(userId);
    props.getUser(userId);
    
    getEmployeeDetails(userId, (response: EmployeeDetailDto) => setEmployeeDetails(response), mounted);

    setCellWidth(props.verticalDisplay ? 12 : 4);
  }, [props.internetUserId]);

  useEffect(() => () => { mounted.current = false; }, []);

  let isLoading = props.isFetchingLoginStatistics ||
    props.isFetchingInternetUser ||
    props.loginStatistics == null ||
    Object.keys(props.internetUser).length === 0 ||
    employeeDetails == null;

  if (isLoading) return (<MSpinner />);

  return (
    <Grid className="md-grid-cards">
      <Cell size={cellWidth}>
        <EmployeeDetails internetUserId={internetUserId} />
      </Cell>
      <Cell size={cellWidth}>
        <UserInformation
          userInformation={props.internetUser}
          internetUserId={internetUserId}
          canEditUser={props.canEditUsers}
        />
      </Cell>
      <Cell size={cellWidth}>
        <LoginActivity loginStatistics={props.loginStatistics} />
      </Cell>
      <Cell size={cellWidth === 4 ? 6 : 12}>
        <EmployeeTEAM internetUserId={internetUserId} />
      </Cell>
      {props.showTeamManagementCard &&
        <Cell size={cellWidth}>
          <TeamMemberManagement internetUserId={internetUserId} userRights={props.internetUser.userRights} supervisorName={props.supervisorName} />
        </Cell>}
      <Cell size={cellWidth}>
        <EmployeeMappings employeeId={employeeDetails.employeeId} overrideQueueTypes={[homeownersQaQueueTypeMSelectOption ,auditQAQueueTypeMSelectOption]} />
      </Cell>
      <Cell size={cellWidth === 4 ? 6 : 12}>
        <MCard
          title="Employee Calendar"
          fields={[{
            key: "calendar",
            control: (
              <EmployeeCalendar
                employeeId={employeeDetails.employeeId}
                readOnly={props.canEditCalendarAsManager ? false : true}
                allowManagementEdit={props.canEditCalendarAsManager}
              />)
          }]}
        />
      </Cell>
      {props.canViewEmployeeCourses &&
        <Cell size={12}>
          <EmployeeCourses employeeId={employeeDetails.employeeId} />
        </Cell>}
      {props.canViewMacBonus &&
        <Cell size={12} className="mac-list-cell">
          <QAMacCard
            internetUserId={internetUserId}
          />
        </Cell>
      }
    </Grid>
  );
};

const mapStateToProps = state => {
  return {
    canEditCalendarAsManager: userHasRight(MANAGEMENT_CALENDAR_EDIT)(state),
    showTeamManagementCard: userHasRight(QA_TEAM_MAINTENANCE)(state),
    canViewEmployeeCourses: userHasRight(EMPLOYEE_COURSE_VIEW)(state),
    canViewMacBonus: userHasRight(BUSOPS_AGENT_MAC_BONUS_SHOW)(state),
    canEditUsers: userHasRight(AUTHENTICATION_EDIT)(state),
    isFetchingInternetUser: state.user.isFetchingInternetUser,
    isFetchingLoginStatistics: state.user.isFetchingLoginStatistics,
    loginStatistics: state.user.loginStatistics,
    internetUser: state.user.internetUser,
  };
};

export default withRouter(connect(mapStateToProps)(AgentProfile));
